import { DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, map, Observable, tap, throwError } from 'rxjs';
import { RoleClient, RoleResponse } from '../../../core/openapi/identity';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { outputError } from '../../../shared/utils/output-error.function';
import { LoadingState } from '../../../shared/types/loading.type';

@Injectable()
export class RoleManagementService {
  private readonly roleClient = inject(RoleClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _loadingState: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );

  public readonly loadingState: Signal<LoadingState> = this._loadingState.asReadonly();

  public createRole(name: string, permissionIds: string[]): Observable<void> {
    this._loadingState.set(LoadingState.LOADING);

    return this.roleClient.rolePost({ name, permissionIds }).pipe(
      map((): void => undefined),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant('monitoring.roles.modal.toast.role-created', {
            name,
          }),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.roles.modal.toast.role-creation-failed',
            {
              name,
              error: outputError(error, this.translateService.instant('errors.unknown')),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public updateRole(id: string, name: string, permissionIds: string[]): Observable<void> {
    this._loadingState.set(LoadingState.LOADING);

    return this.roleClient.rolePut({ id, name, permissionIds }).pipe(
      map((): void => undefined),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant('monitoring.roles.modal.toast.role-updated', {
            name,
          }),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.roles.modal.toast.role-update-failed',
            {
              name,
              error: outputError(error, this.translateService.instant('errors.unknown')),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public deleteRole(id: string, name: string): Observable<void> {
    this._loadingState.set(LoadingState.LOADING);

    return this.roleClient.roleIdDelete(id).pipe(
      map((): void => undefined),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant('monitoring.roles.modal.toast.role-removed', {
            name,
          }),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.roles.modal.toast.role-deletion-failed',
            {
              name,
              error: outputError(error, this.translateService.instant('errors.unknown')),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public loadRoleDetails(roleId: string): Observable<RoleResponse> {
    if (roleId.length === 0) {
      return throwError(() => new Error('No role id provided'));
    }

    this._loadingState.set(LoadingState.LOADING);

    return this.roleClient.roleIdGet(roleId).pipe(
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.roles.modal.toast.role-reading-failed',
            {
              error: outputError(error, this.translateService.instant('errors.unknown')),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }
}
